import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

interface MissionModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface Mission {
  id: string;
  title: string;
  description: string;
  reward: number;
  type: 'daily' | 'weekly';
  action_type: string;
  target_count: number;
  icon: string;
}

export default function MissionModal({ onClose, onSuccess }: MissionModalProps) {
  const { user } = useAuth();
  const [missions, setMissions] = useState<Mission[]>([]);
  const [completedMissions, setCompletedMissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMissions();
  }, []);

  const loadMissions = async () => {
    if (!user) return;

    try {
      // 미션 목록 조회
      const { data: missionData, error: missionError } = await supabase
        .from('talent_missions')
        .select('*')
        .eq('is_active', true)
        .order('type')
        .order('reward', { ascending: false });

      if (missionError) throw missionError;
      setMissions(missionData || []);

      // 오늘 완료한 미션 조회
      const today = new Date().toISOString().split('T')[0];
      const { data: completionData, error: completionError } = await supabase
        .from('mission_completions')
        .select('mission_id')
        .eq('user_id', user.id)
        .gte('completed_at', `${today}T00:00:00`)
        .lte('completed_at', `${today}T23:59:59`);

      if (completionError) throw completionError;
      setCompletedMissions(completionData?.map(c => c.mission_id) || []);
    } catch (error) {
      console.error('미션 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteMission = async (mission: Mission) => {
    if (!user) return;

    try {
      // 미션 완료 기록
      await supabase
        .from('mission_completions')
        .insert({
          user_id: user.id,
          mission_id: mission.id,
        });

      // 달란트 적립
      const { data: pointData, error: pointError } = await supabase
        .from('talent_points')
        .select('balance, total_earned')
        .eq('user_id', user.id)
        .single();

      if (pointError) throw pointError;

      await supabase
        .from('talent_points')
        .update({
          balance: pointData.balance + mission.reward,
          total_earned: pointData.total_earned + mission.reward,
        })
        .eq('user_id', user.id);

      // 거래 내역 생성
      await supabase
        .from('point_transactions')
        .insert({
          user_id: user.id,
          amount: mission.reward,
          type: 'reward',
          description: `미션 완료: ${mission.title}`,
        });

      alert(`${mission.reward.toLocaleString()} 달란트를 획득했습니다!`);
      loadMissions();
      onSuccess();
    } catch (error) {
      console.error('미션 완료 실패:', error);
      alert('미션 완료 처리 중 오류가 발생했습니다.');
    }
  };

  const dailyMissions = missions.filter(m => m.type === 'daily');
  const weeklyMissions = missions.filter(m => m.type === 'weekly');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-900">달란트 미션</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-teal-600 border-t-transparent"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* 일일 미션 */}
            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-sun-line text-yellow-500"></i>
                일일 미션
              </h4>
              <div className="space-y-3">
                {dailyMissions.map((mission) => {
                  const isCompleted = completedMissions.includes(mission.id);
                  return (
                    <div
                      key={mission.id}
                      className={`border rounded-xl p-4 ${
                        isCompleted ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-300'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                            isCompleted ? 'bg-gray-200' : 'bg-teal-100'
                          }`}>
                            <i className={`${mission.icon} text-2xl ${
                              isCompleted ? 'text-gray-400' : 'text-teal-600'
                            }`}></i>
                          </div>
                          <div className="flex-1">
                            <h5 className={`font-semibold mb-1 ${
                              isCompleted ? 'text-gray-500' : 'text-gray-900'
                            }`}>
                              {mission.title}
                            </h5>
                            <p className={`text-sm mb-2 ${
                              isCompleted ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                              {mission.description}
                            </p>
                            <div className="flex items-center gap-2">
                              <span className={`text-sm font-semibold ${
                                isCompleted ? 'text-gray-400' : 'text-teal-600'
                              }`}>
                                +{mission.reward.toLocaleString()} 달란트
                              </span>
                            </div>
                          </div>
                        </div>
                        {isCompleted ? (
                          <div className="px-4 py-2 bg-gray-200 text-gray-500 rounded-lg text-sm font-semibold whitespace-nowrap">
                            완료
                          </div>
                        ) : (
                          <button
                            onClick={() => handleCompleteMission(mission)}
                            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            완료하기
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 주간 미션 */}
            {weeklyMissions.length > 0 && (
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-calendar-line text-blue-500"></i>
                  주간 미션
                </h4>
                <div className="space-y-3">
                  {weeklyMissions.map((mission) => {
                    const isCompleted = completedMissions.includes(mission.id);
                    return (
                      <div
                        key={mission.id}
                        className={`border rounded-xl p-4 ${
                          isCompleted ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-300'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                              isCompleted ? 'bg-gray-200' : 'bg-blue-100'
                            }`}>
                              <i className={`${mission.icon} text-2xl ${
                                isCompleted ? 'text-gray-400' : 'text-blue-600'
                              }`}></i>
                            </div>
                            <div className="flex-1">
                              <h5 className={`font-semibold mb-1 ${
                                isCompleted ? 'text-gray-500' : 'text-gray-900'
                              }`}>
                                {mission.title}
                              </h5>
                              <p className={`text-sm mb-2 ${
                                isCompleted ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                {mission.description}
                              </p>
                              <div className="flex items-center gap-2">
                                <span className={`text-sm font-semibold ${
                                  isCompleted ? 'text-gray-400' : 'text-blue-600'
                                }`}>
                                  +{mission.reward.toLocaleString()} 달란트
                                </span>
                              </div>
                            </div>
                          </div>
                          {isCompleted ? (
                            <div className="px-4 py-2 bg-gray-200 text-gray-500 rounded-lg text-sm font-semibold whitespace-nowrap">
                              완료
                            </div>
                          ) : (
                            <button
                              onClick={() => handleCompleteMission(mission)}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                            >
                              완료하기
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
